import { useState, useEffect, useRef, Fragment, useCallback } from 'react';

import Chip from '@zola/zola-ui/src/components/Chip';
import { CheckboxField } from '@zola/zola-ui/src/components/Form/CheckboxField';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import { useResponsiveDesign } from '@zola/zola-ui/src/contexts/ResponsiveDesignContext/ResponsiveDesignContext';
import useClickOutside from '@zola/zola-ui/src/hooks/useClickOutside';

import cx from 'classnames';
import _filter from 'lodash/filter';
import _map from 'lodash/map';
import _some from 'lodash/some';

import { parseSortingValues } from '~/pages/couples/explore/components/SearchResults/components/ManualSortingOptions/ManualSortingOptions';
import { useSearchResultsPageContext } from '~/pages/couples/explore/components/SearchResults/contexts/SearchResultsPageContext';
import { createVendorSearchFromFilters } from '~/pages/couples/explore/util/createVendorSearchFromFilters';
import { OptionFacetCategoryType, OptionFacetType } from '~/types/types';

import { useVendorSearchNavigation } from '../../../../contexts/VendorSearchNavigationContext';
import PriceFacetForm from '../PriceFacetForm/PriceFacetForm';

import styles from './facetButton.module.less';

import './FacetButton.less';

interface MappedOptionFacetType extends OptionFacetType {
  checked: boolean;
}
/**
 * A button for choosing facets from a category
 *
 * @param {*} props
 */

export interface FacetButtonProps {
  category: OptionFacetCategoryType;
  openSideDrawer: () => void;
  isImportant?: boolean;
  className?: string;
}

const SingleSelectFacetList = ({
  childFacet,
  category,
  toggleCheckbox,
  setIsOpen,
}: {
  category: OptionFacetCategoryType;
  childFacet: MappedOptionFacetType;
  toggleCheckbox: (option: OptionFacetType) => void;
  setIsOpen: (value: boolean) => void;
}) => {
  const { navigateToSrp } = useVendorSearchNavigation();

  const { slug, flavorText, displayName: facetDisplayName, checked, name: facetName } = childFacet;
  return (
    <li key={`${slug}`}>
      <LinkV2
        role="button"
        type="button"
        noUnderline
        id={`${category.displayName}-${slug}`}
        className={cx('facet-button__single-select', {
          selected: checked,
        })}
        onClick={() => {
          if (isSortingFilter(category)) {
            const [sortBy, sortOrder] = parseSortingValues(childFacet.slug);
            if (sortBy && sortOrder) {
              navigateToSrp({ sorting: { sortBy, sortOrder } });
            } else {
              navigateToSrp({ sorting: null });
            }
          } else {
            toggleCheckbox(childFacet);
          }
          setIsOpen(false);
        }}
      >
        {flavorText && `${flavorText}: `}
        {facetDisplayName || facetName}
      </LinkV2>
    </li>
  );
};

const MultiSelectFacetList = ({
  childFacet,
  previousHasDescription,
  toggleCheckbox,
  category,
}: {
  childFacet: MappedOptionFacetType;
  previousHasDescription: boolean | undefined;
  toggleCheckbox: (option: OptionFacetType) => void;
  category: OptionFacetCategoryType;
}) => {
  const { slug, flavorText, displayName: facetDisplayName, name: facetName } = childFacet;
  return (
    <li key={`${slug}`}>
      <CheckboxField
        className={cx('facet-button__checkbox', {
          'with-description': previousHasDescription && Boolean(childFacet.description),
        })}
        checked={childFacet.checked}
        onChange={() => {
          toggleCheckbox(childFacet);
        }}
        id={`${category.displayName}-${slug}`}
        label={
          <Fragment>
            <div className="facet-button__name">
              {flavorText && `${flavorText}: `}
              {facetDisplayName || facetName}
            </div>
            {childFacet.description ? (
              <Fragment>
                <div className="facet-button__description">{childFacet.description}</div>
              </Fragment>
            ) : null}
          </Fragment>
        }
      />
      {childFacet.description ? <hr /> : null}
    </li>
  );
};

export const isSortingFilter = (category: OptionFacetCategoryType) => category.key === 'sorting';

const FacetButton = ({
  category,
  openSideDrawer,
  isImportant,
  className,
}: FacetButtonProps): JSX.Element => {
  const { displayName, key, facets } = category;
  const { isDesktop } = useResponsiveDesign();

  const plannerParentKey = 'planner-service-level';
  const isPlannerServiceLevel = key === plannerParentKey;
  const isPriceFilter = key === 'price';

  const [isOpen, setIsOpen] = useState(false);
  const node = useRef<HTMLHeadingElement>(null);
  const { selectedFacetOptions } = useSearchResultsPageContext();
  const { navigateToSrp } = useVendorSearchNavigation();

  const setSelectedFilters = useCallback(
    (options: OptionFacetType[]) => navigateToSrp(createVendorSearchFromFilters(options)),
    [navigateToSrp]
  );

  const toggleCheckbox = useCallback(
    (option: OptionFacetType) => {
      const checked = _some(
        selectedFacetOptions,
        (facetOption) => facetOption.slug === option.slug
      );
      if (!checked) {
        setSelectedFilters([...selectedFacetOptions, option]);
      } else {
        setSelectedFilters(_filter(selectedFacetOptions, (f) => f.slug !== option.slug));
      }
      if (isPlannerServiceLevel) {
        const limitedFacetOptions = _filter(
          selectedFacetOptions,
          (limitedOption) => limitedOption.parentKey !== plannerParentKey
        );
        setSelectedFilters([...limitedFacetOptions, option]);
      }
    },
    [isPlannerServiceLevel, selectedFacetOptions, setSelectedFilters]
  );

  const toggleDropdown = useCallback(() => {
    if (!isDesktop) {
      openSideDrawer();
      return;
    }
    setIsOpen(!isOpen);
  }, [isDesktop, isOpen, openSideDrawer]);

  const onClickOutside = useCallback(() => {
    setIsOpen(false);
  }, []);

  useClickOutside({ ref: node, isOpen, onClickOutside });

  const mappedFacets = _map(facets, (childFacet) => ({
    ...childFacet,
    checked: isSortingFilter(category)
      ? childFacet.key === category.slug
      : _some(selectedFacetOptions, (facetOption) => facetOption.slug === childFacet.slug),
  }));

  const currentlySelectedFilters = mappedFacets.filter(({ checked }) => checked).length;

  const isPriceFacetActive =
    isPriceFilter && _some(selectedFacetOptions, (facet) => facet.slug.includes('price'));
  const isActive = _some(mappedFacets, (facet) => facet.checked) || isPriceFacetActive;

  const classNames = cx(
    'facet-button__button',
    {
      active: isActive,
      'facet-button__sorting': isSortingFilter(category),
    },
    className
  );

  const currentlySelectedFilterCount =
    currentlySelectedFilters && !isSortingFilter(category) ? `(${currentlySelectedFilters})` : '';

  const isSingleSelectDropdown = isPlannerServiceLevel || isSortingFilter(category);

  const ref = useRef<HTMLDivElement>(null);

  // Force the scroll bar to be shown for dropdowns that are more than 330 px high, so the user knows they scroll
  const [showScroll, setShowScroll] = useState(true);
  useEffect(() => {
    setShowScroll(isOpen && !!ref.current && ref.current.offsetHeight >= 330);
  }, [isOpen]);

  return (
    <div className="facet-button__container" ref={node} data-is-important={isImportant}>
      <Chip
        data-testid="search-facet-button"
        className={classNames}
        selected={isOpen}
        active={isActive}
        onClick={toggleDropdown}
        withCaret={isDesktop}
      >
        {`${displayName} ${currentlySelectedFilterCount}`}
      </Chip>
      {isOpen && (
        <div
          ref={ref}
          className={cx('facet-button__dropdown', {
            'price-dropdown': isPriceFilter,
            'show-scroll': showScroll,
          })}
        >
          <ul>
            {isPriceFilter ? (
              <PriceFacetForm
                setIsOpen={setIsOpen}
                showSubmit
                verticalStack={false}
                className={styles.priceFacetForm}
              />
            ) : (
              <ul>
                {mappedFacets.map((childFacet, i) => {
                  if (isSingleSelectDropdown) {
                    return (
                      <SingleSelectFacetList
                        category={category}
                        childFacet={childFacet}
                        key={childFacet.slug}
                        toggleCheckbox={toggleCheckbox}
                        setIsOpen={setIsOpen}
                      />
                    );
                  }
                  return (
                    <MultiSelectFacetList
                      category={category}
                      childFacet={childFacet}
                      key={childFacet.slug}
                      previousHasDescription={Boolean(mappedFacets[i - 1]?.description)}
                      toggleCheckbox={toggleCheckbox}
                    />
                  );
                })}
              </ul>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default FacetButton;
